import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Pagination, Tooltip } from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';

import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockIcon from '@mui/icons-material/Lock';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';

import { AesAnalyzerAPI, AesRun } from 'api/AesAnalyzerAPI';
import CreditsAPI from 'api/CreditsAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import RunAesAnalysisModal from 'components/Analyzers/RunAesAnalysisModal';
import DeleteConfirmationModal from 'components/Analyzers/DeleteConfirmationModal';
import AesSeverityChip, {
  AES_SEVERITY_ORDER,
} from 'components/Analyzers/AesSeverityChip';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import 'resources/styles/aes-analyzer.css';

const PAGE_SIZE = 10;

const SEVERITY_GUIDE: Record<string, string> = {
  critical:
    'Plausible license/sanctions violation or a defect AES would reject. Review first.',
  high: 'Filing-integrity or licensing-completeness defect.',
  medium: 'Data conflicts with expectation; may or may not be wrong.',
  low: 'Weak signal; verify.',
  informational: 'Context for the reviewer; not a defect on its own.',
};

const isInProgress = (run: AesRun) =>
  run.status === 'Running' || run.status === 'Pending';

const getStatusBadge = (run: AesRun) => {
  let className = 'run-badge ';
  if (run.status === 'Completed') className += 'completed';
  else if (run.status === 'Needs Review') className += 'needs-review';
  else if (isInProgress(run)) className += 'running';
  else if (run.status === 'Failed') className += 'failed';
  const badge = <span className={className}>{run.status}</span>;
  // The worker reads the file after the upload, so an unreadable file or a
  // short credit balance shows up here, as the run's failure reason.
  return run.status === 'Failed' && run.error_message ? (
    <Tooltip title={run.error_message}>{badge}</Tooltip>
  ) : (
    badge
  );
};

const AesAnalyzerPage: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId: string | undefined = cur_user?.selected_team?.id;

  const [runs, setRuns] = useState<AesRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All Runs');
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [newAnalysisModal, setNewAnalysisModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [runToDelete, setRunToDelete] = useState<AesRun | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'created_at', direction: 'desc' });

  // License gate — same tiers as the ACE Analyzer.
  const requiredTiers = ['pro', 'business', 'enterprise'];
  const userHasAccess =
    requiredTiers.includes(cur_user?.license) ||
    cur_user?.bite_super_admin === true;
  const isInteractionDisabled = !userHasAccess;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  // Used by the upload modal to show the most relevant balance before the file
  // is parsed. The worker resolves the real pool from current balances once it
  // knows the exact shipment cost: trial when it covers the whole run, otherwise
  // paid, without splitting a run between pools.
  const [creditSpendMode, setCreditSpendMode] = useState<'trial' | 'active'>(
    'active',
  );
  useEffect(() => {
    let isMounted = true;
    setCreditSpendMode('active');
    if (!groupId) return undefined;
    CreditsAPI.getTrialCreditsSummary(groupId)
      .then((response: any) => {
        const trial = Array.isArray(response?.result)
          ? response.result[0]
          : undefined;
        if (isMounted && Number(trial?.remaining_credits ?? 0) > 0) {
          setCreditSpendMode('trial');
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to resolve AES credit pool:', error);
      });
    return () => {
      isMounted = false;
    };
  }, [groupId]);

  // Bumped whenever the team changes (and on unmount), so a slow response or
  // a poll started for the previous team can never overwrite this team's runs.
  const fetchGeneration = useRef(0);

  const fetchRuns = async () => {
    const generation = fetchGeneration.current;
    if (isInteractionDisabled || !groupId) {
      setRuns([]);
      setLoading(false);
      return;
    }
    try {
      const data = await AesAnalyzerAPI.getRuns(groupId);
      if (generation === fetchGeneration.current) setRuns(data);
    } catch (error) {
      if (generation === fetchGeneration.current) {
        console.error('Failed to fetch AES runs:', error);
      }
    } finally {
      if (generation === fetchGeneration.current) setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchRuns();
    return () => {
      fetchGeneration.current += 1;
    };
  }, [groupId, isInteractionDisabled]);

  // Smart poller: refresh quietly only while a run is still processing.
  // groupId is a dependency so a team switch clears the old team's timer.
  useEffect(() => {
    if (isInteractionDisabled || !groupId) return undefined;
    const hasActiveRuns = runs.some(isInProgress);
    if (!hasActiveRuns) return undefined;
    const timeoutId = setTimeout(fetchRuns, 4000);
    return () => clearTimeout(timeoutId);
  }, [runs, groupId, isInteractionDisabled]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const processedRuns = useMemo(() => {
    let result = [...runs];
    if (activeTab === 'Running') {
      result = result.filter(isInProgress);
    } else if (activeTab !== 'All Runs') {
      result = result.filter((r) => r.status === activeTab);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((r) => (r.name || '').toLowerCase().includes(q));
    }
    const numeric = new Set([
      'line_count',
      'flagged_line_count',
      'critical_count',
    ]);
    result.sort((a: any, b: any) => {
      let av: any = a[sortConfig.key];
      let bv: any = b[sortConfig.key];
      if (numeric.has(sortConfig.key)) {
        av = Number(av || 0);
        bv = Number(bv || 0);
      } else if (sortConfig.key === 'created_at') {
        av = new Date(av || 0).getTime();
        bv = new Date(bv || 0).getTime();
      } else {
        av = String(av || '').toLowerCase();
        bv = String(bv || '').toLowerCase();
      }
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [runs, activeTab, searchQuery, sortConfig]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(processedRuns.length / PAGE_SIZE));
  const paginatedRuns = processedRuns.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // KPIs
  const totalLines = runs.reduce((s, r) => s + Number(r.line_count || 0), 0);
  const needsReviewCount = runs.filter(
    (r) => r.status === 'Needs Review',
  ).length;
  const inProgressCount = runs.filter(isInProgress).length;

  const handleRunAnalysis = async (name: string, file: File) => {
    if (!groupId) throw new Error('No team selected.');
    const run = await AesAnalyzerAPI.createRun(
      groupId,
      name,
      file,
      creditSpendMode,
    );
    enqueueSnackbar('AES analysis queued', { variant: 'success' });
    setNewAnalysisModal(false);
    setRuns((prev) => [run, ...prev.filter((r) => r.id !== run.id)]);
  };

  const confirmDeleteRun = (e: React.MouseEvent, run: AesRun) => {
    e.stopPropagation();
    if (isInteractionDisabled) return;
    setRunToDelete(run);
    setDeleteModalOpen(true);
  };

  const executeDeleteRun = async () => {
    if (!runToDelete || !groupId) return;
    setIsDeleting(true);
    try {
      await AesAnalyzerAPI.deleteRun(runToDelete.id, groupId);
      enqueueSnackbar('Analysis deleted', { variant: 'success' });
      setDeleteModalOpen(false);
      setRunToDelete(null);
      setRuns((prev) => prev.filter((r) => r.id !== runToDelete.id));
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to delete analysis',
        { variant: 'error' },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async (e: React.MouseEvent, run: AesRun) => {
    e.stopPropagation();
    if (!groupId || downloadingId) return;
    setDownloadingId(run.id);
    try {
      await AesAnalyzerAPI.downloadFindingsCsv(run.id, groupId, run.name);
    } catch (error) {
      enqueueSnackbar('Failed to download findings', { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const isSettled = (run: AesRun) =>
    run.status === 'Completed' || run.status === 'Needs Review';

  const sortHeader = (label: string, key: string) => (
    <th
      onClick={() => handleSort(key)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {label}
        {sortConfig.key === key &&
          (sortConfig.direction === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          ))}
      </div>
    </th>
  );

  const rowActions = (run: AesRun) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
      }}
    >
      <Tooltip title='Download Findings (CSV)'>
        <span>
          <button
            onClick={(e) => handleDownload(e, run)}
            disabled={
              isInteractionDisabled || !isSettled(run) || !!downloadingId
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--full-bar-color)',
              cursor:
                isInteractionDisabled || !isSettled(run)
                  ? 'not-allowed'
                  : 'pointer',
              opacity: isInteractionDisabled || !isSettled(run) ? 0.4 : 1,
            }}
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 24 }} />
          </button>
        </span>
      </Tooltip>
      <Tooltip title='Delete Analysis'>
        <span>
          <button
            onClick={(e) => confirmDeleteRun(e, run)}
            disabled={isInteractionDisabled || isInProgress(run)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--red-color, #ef4444)',
              cursor:
                isInteractionDisabled || isInProgress(run)
                  ? 'not-allowed'
                  : 'pointer',
              opacity: isInteractionDisabled || isInProgress(run) ? 0.4 : 1,
            }}
          >
            <DeleteOutlineIcon style={{ fontSize: 24 }} />
          </button>
        </span>
      </Tooltip>
    </div>
  );

  const criticalCell = (run: AesRun) =>
    isSettled(run) && Number(run.critical_count) > 0 ? (
      <AesSeverityChip
        severity='critical'
        label={Number(run.critical_count).toLocaleString()}
      />
    ) : (
      <span style={{ color: 'var(--text-tertiary)' }}>
        {isSettled(run) ? 0 : '--'}
      </span>
    );

  return (
    <div className='analyzers-dashboard-root'>
      {/* Header */}
      <div className='detail-header-flex'>
        <div>
          <h1 className='analyzers-page-title'>AES Analyzer</h1>
          <p className='analyzers-page-subtitle'>
            Audit your AES export filings for classification, licensing,
            destination, timeliness and PGA issues.
          </p>
        </div>
        <div className='detail-header-actions'>
          <button
            className='btn-primary'
            onClick={() => !isInteractionDisabled && setNewAnalysisModal(true)}
            disabled={isInteractionDisabled}
            style={{
              cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
              opacity: isInteractionDisabled ? 0.6 : 1,
            }}
          >
            {isInteractionDisabled && (
              <LockIcon style={{ fontSize: 16, marginRight: 6 }} />
            )}
            <BoltIcon style={{ fontSize: 18 }} /> Run New Analysis
          </button>
        </div>
      </div>

      {!userHasAccess && (
        <RequiredLicense userLicense={normalizedLicense} minimumLicense='pro' />
      )}

      {/* KPI Row */}
      <div className='detail-kpi-grid'>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <BarChartIcon style={{ fontSize: 16, color: '#4895ef' }} />
            <span className='kpi-card-label'>Total Runs</span>
          </div>
          <div className='kpi-card-value text-blue'>
            {runs.length.toLocaleString()}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <TextSnippetIcon style={{ fontSize: 16, color: '#20c997' }} />
            <span className='kpi-card-label'>Lines Audited</span>
          </div>
          <div className='kpi-card-value text-teal'>
            {totalLines.toLocaleString()}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <WarningAmberIcon style={{ fontSize: 16, color: '#f79009' }} />
            <span className='kpi-card-label'>Needs Review</span>
          </div>
          <div className='kpi-card-value text-orange'>
            {needsReviewCount.toLocaleString()}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <AccessTimeIcon style={{ fontSize: 16, color: '#4895ef' }} />
            <span className='kpi-card-label'>In Progress</span>
          </div>
          <div className='kpi-card-value text-blue'>
            {inProgressCount.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Guide */}
      <div
        style={{ width: '100%', padding: '0 24px', boxSizing: 'border-box' }}
      >
        <button
          type='button'
          onClick={() => setShowGuide((prev) => !prev)}
          style={{
            width: '100%',
            background: 'var(--Background-200, var(--bg-colors))',
            border: '1px solid var(--base-border)',
            borderRadius: '8px',
            minHeight: '44px',
            color: 'var(--text-primary)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '16px',
              fontWeight: 500,
            }}
          >
            <HelpOutlineOutlinedIcon
              sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
            />
            New to the AES Analyzer? Learn how severities work and what to
            upload
          </span>
          {showGuide ? (
            <ExpandLessOutlinedIcon
              sx={{ fontSize: 20, color: 'var(--card-text)' }}
            />
          ) : (
            <ExpandMoreOutlinedIcon
              sx={{ fontSize: 20, color: 'var(--card-text)' }}
            />
          )}
        </button>

        {showGuide && (
          <div
            style={{
              display: 'flex',
              gap: '16px',
              marginTop: '16px',
              flexWrap: 'wrap',
            }}
          >
            <div className='aes-panel' style={{ flex: '1 1 420px' }}>
              <div className='aes-panel-header'>
                <h3 className='aes-panel-title'>Severity Guide</h3>
              </div>
              <p className='aes-panel-subtitle' style={{ marginBottom: 12 }}>
                Each flag carries one of five severities. Work Critical first;
                Informational flags are context, not defects.
              </p>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                {AES_SEVERITY_ORDER.map((sev) => (
                  <div
                    key={sev}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '112px 1fr',
                      gap: 12,
                      alignItems: 'center',
                    }}
                  >
                    <AesSeverityChip severity={sev} />
                    <span className='card-db-meta-span'>
                      {SEVERITY_GUIDE[sev]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className='aes-panel' style={{ flex: '1 1 420px' }}>
              <div className='aes-panel-header'>
                <h3
                  className='aes-panel-title'
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <TipsAndUpdatesOutlinedIcon
                    sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
                  />
                  Upload Tips
                </h3>
              </div>
              <ol
                className='card-db-meta-span'
                style={{ margin: '0 0 0 20px', lineHeight: '1.6' }}
              >
                <li>One row per commodity line, as exported from AES.</li>
                <li>
                  Use AESTIR field names (License Type Code, Export Control
                  Classification Number, Country of Ultimate Destination Code…)
                  — download the template for the full list.
                </li>
                <li>
                  Include the PGA indicator and Filing Option columns if your
                  platform exports them; without them some checks can only flag
                  lines for confirmation.
                </li>
                <li>
                  Extra columns of your own (PO numbers, internal references…)
                  are fine — they are kept and returned in the findings export.
                </li>
                <li>
                  Every run shows which checks ran, ran partially, or could not
                  run — a clean line is never assumed.
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Runs */}
      <div className='analyzers-section'>
        <div className='analyzers-section-header'>
          <h2 className='section-title'>Analysis Runs</h2>
          <p className='section-subtitle'>
            Click a run to review its findings line by line.
          </p>
        </div>

        <div
          className='runs-toolbar'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
        >
          <div className='runs-tabs'>
            <div className='data-search-wrapper'>
              <SearchIcon
                className='data-search-icon'
                style={{ fontSize: 18 }}
              />
              <input
                type='text'
                className='data-search-input'
                placeholder='Search by Name'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isInteractionDisabled}
              />
            </div>
            {['All Runs', 'Completed', 'Needs Review', 'Running', 'Failed'].map(
              (tab) => (
                <button
                  key={tab}
                  className={`runs-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => !isInteractionDisabled && setActiveTab(tab)}
                  disabled={isInteractionDisabled}
                  style={{
                    cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                    opacity: isInteractionDisabled ? 0.6 : 1,
                  }}
                >
                  {tab}
                </button>
              ),
            )}
          </div>

          <div className='view-toggle-group'>
            <button
              className={`view-toggle-btn ${
                viewMode === 'list' ? 'active' : ''
              }`}
              onClick={() => setViewMode('list')}
              title='List View'
            >
              <ViewListIcon style={{ fontSize: 20 }} />
            </button>
            <button
              className={`view-toggle-btn ${
                viewMode === 'card' ? 'active' : ''
              }`}
              onClick={() => setViewMode('card')}
              title='Card View'
            >
              <GridViewIcon style={{ fontSize: 20 }} />
            </button>
          </div>
        </div>

        <div
          className='runs-content-container'
          style={
            viewMode === 'card'
              ? { background: 'transparent', border: 'none' }
              : {}
          }
        >
          {viewMode === 'list' && (
            <table className='runs-table'>
              <thead>
                <tr>
                  {sortHeader('Run Name', 'name')}
                  <th>Status</th>
                  {sortHeader('Uploaded By', 'initiated_by_name')}
                  {sortHeader('Lines', 'line_count')}
                  {sortHeader('Flagged Lines', 'flagged_line_count')}
                  {sortHeader('Critical', 'critical_count')}
                  {sortHeader('Date', 'created_at')}
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  runs.length === 0 &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={`sk-${i}`}>
                      <td colSpan={8}>
                        <div
                          style={{
                            height: 16,
                            borderRadius: 4,
                            background: 'var(--bg-surface)',
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                {paginatedRuns.map((run) => (
                  <tr
                    key={run.id}
                    className='hoverable-row'
                    onClick={() =>
                      !isInteractionDisabled &&
                      history.push(`/aes-analyzers/${run.id}`)
                    }
                    style={{
                      cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <td className='run-name'>{run.name}</td>
                    <td>{getStatusBadge(run)}</td>
                    <td>{run.initiated_by_name || '--'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number(run.line_count || 0).toLocaleString()}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {isSettled(run)
                        ? Number(run.flagged_line_count || 0).toLocaleString()
                        : '--'}
                    </td>
                    <td>{criticalCell(run)}</td>
                    <td>
                      {run.created_at
                        ? moment(run.created_at).format('MM/DD/YYYY')
                        : '--'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{rowActions(run)}</td>
                  </tr>
                ))}
                {!loading && paginatedRuns.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ textAlign: 'center', padding: '24px' }}
                    >
                      No runs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {viewMode === 'card' && (
            <div className='card-grid-three'>
              {paginatedRuns.map((run) => (
                <div
                  key={run.id}
                  className='card-db hoverable-card'
                  onClick={() =>
                    !isInteractionDisabled &&
                    history.push(`/aes-analyzers/${run.id}`)
                  }
                  style={{
                    minHeight: '160px',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <div
                    className='card-db-header'
                    style={{
                      marginBottom: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div>
                      <div
                        className='card-db-title'
                        style={{
                          fontSize: '16px',
                          lineHeight: '1.4',
                          marginBottom: '8px',
                        }}
                      >
                        {run.name}
                      </div>
                      {getStatusBadge(run)}
                    </div>
                    {rowActions(run)}
                  </div>
                  <div
                    className='card-db-meta-col'
                    style={{
                      alignItems: 'flex-start',
                      gap: '8px',
                      marginBottom: '16px',
                    }}
                  >
                    {[
                      ['Lines', Number(run.line_count || 0).toLocaleString()],
                      [
                        'Flagged Lines',
                        isSettled(run)
                          ? Number(run.flagged_line_count || 0).toLocaleString()
                          : '--',
                      ],
                      ['Critical', criticalCell(run)],
                      [
                        'Date',
                        run.created_at
                          ? moment(run.created_at).format('MM/DD/YYYY')
                          : '--',
                      ],
                    ].map(([label, value], i) => (
                      <span
                        key={i}
                        className='card-db-meta-span'
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <span>{label}:</span>
                        <strong style={{ color: 'var(--text-primary-color)' }}>
                          {value}
                        </strong>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {processedRuns.length > 0 && (
            <div
              className='hb-pagination'
              style={
                viewMode === 'list'
                  ? {
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '16px',
                      flexWrap: 'wrap',
                      padding: '16px 24px',
                      borderTop: '1px solid var(--es-soft-border)',
                      background: 'var(--Background-200, var(--bg-colors))',
                    }
                  : {
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '24px',
                    }
              }
            >
              {viewMode === 'list' && (
                <span
                  className='pagination-text'
                  style={{ color: 'var(--text-secondary)', fontSize: '14px' }}
                >
                  Page {currentPage} of {totalPages}
                </span>
              )}
              <Pagination
                onChange={(_e: React.ChangeEvent<unknown>, page: number) =>
                  setCurrentPage(page)
                }
                size='large'
                count={totalPages}
                shape='rounded'
                variant='text'
                page={currentPage}
                disabled={isInteractionDisabled}
              />
            </div>
          )}
        </div>
      </div>

      <RunAesAnalysisModal
        open={newAnalysisModal}
        handleClose={() => setNewAnalysisModal(false)}
        onAnalyze={handleRunAnalysis}
        teamId={groupId || ''}
        creditSpendMode={creditSpendMode}
      />
      <DeleteConfirmationModal
        open={deleteModalOpen}
        handleClose={() => {
          setDeleteModalOpen(false);
          setRunToDelete(null);
        }}
        onDelete={executeDeleteRun}
        isDeleting={isDeleting}
        runName={runToDelete?.name}
      />
    </div>
  );
};

export default AesAnalyzerPage;
